import { useCallback, useRef, useState, type ClipboardEvent, type DragEvent } from 'react';
import { Icon } from './icons';

// Attachments for the people-to-agent channels: a button and a hidden file input, paste and drop onto a textarea,
// uploads that start at once with a chip per file, and the chips a stored note, decision or message shows afterwards
// (images as thumbnails that open the file, documents as a labeled chip). Agents receive what a person attaches.
export type Att = { id: string; kind: string; filename: string; content_type: string; size_bytes: number; text_chars?: number; url: string; text_preview?: string | null };
export type Pending = { local: string; name: string; size: number; kind?: string; preview?: string; att?: Att; error?: string; busy: boolean };

export const ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.md,.csv,.json,.docx,.xlsx,.pptx';
const KIND_LABEL: Record<string, string> = { image: 'Image', pdf: 'PDF', text: 'Text', doc: 'Word', sheet: 'Excel', slides: 'PowerPoint' };
export const fmtBytes = (n: number) => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`);

export async function upload(file: File): Promise<Att> {
  const fd = new FormData(); fd.append('file', file, file.name);
  const res = await fetch('/api/files', { method: 'POST', body: fd });
  let data: any = null; try { data = await res.json(); } catch { /* not json */ }
  if (!res.ok) throw new Error(data?.error || `${res.status} ${res.statusText}`);
  return data.attachment as Att;
}

/** Files from a paste (screenshots, copied images) or a drop. */
export const filesFromPaste = (e: ClipboardEvent): File[] => Array.from(e.clipboardData?.items ?? []).filter((it) => it.kind === 'file').map((it) => it.getAsFile()).filter((f): f is File => !!f);
export const filesFromDrop = (e: DragEvent): File[] => Array.from(e.dataTransfer?.files ?? []);

/** Pending uploads for one composer: add files (they upload at once), remove, the ids ready to send. */
export function useAttach() {
  const [items, setItems] = useState<Pending[]>([]);
  const seq = useRef(0);
  const add = useCallback((files: File[] | FileList) => {
    const list = Array.from(files).filter((f) => f && f.size > 0);
    for (const f of list) {
      const local = `p${++seq.current}`;
      const preview = f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined;
      setItems((xs) => [...xs, { local, name: f.name, size: f.size, preview, busy: true }]);
      upload(f).then((att) => setItems((xs) => xs.map((x) => (x.local === local ? { ...x, att, kind: att.kind, busy: false } : x))))
        .catch((e) => setItems((xs) => xs.map((x) => (x.local === local ? { ...x, error: (e as Error).message, busy: false } : x))));
    }
  }, []);
  const remove = useCallback((local: string) => setItems((xs) => { const x = xs.find((y) => y.local === local); if (x?.preview) URL.revokeObjectURL(x.preview); return xs.filter((y) => y.local !== local); }), []);
  const clear = useCallback(() => setItems((xs) => { xs.forEach((x) => x.preview && URL.revokeObjectURL(x.preview)); return []; }), []);
  const ids = items.filter((x) => x.att).map((x) => x.att!.id);
  const busy = items.some((x) => x.busy);
  const failed = items.filter((x) => x.error).length;
  return { items, add, remove, clear, ids, busy, failed };
}

export function AttachButton({ onFiles, size = 'sm', label = 'Attach' }: { onFiles: (f: FileList) => void; size?: 'sm' | ''; label?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  return (<>
    <button type="button" className={`quiet ${size} attach`} onClick={() => ref.current?.click()} title="Attach images, PDFs, Word, Excel, PowerPoint or text files"><Icon name="clip" />{label}</button>
    <input ref={ref} type="file" multiple accept={ACCEPT} hidden onChange={(e) => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ''; }} />
  </>);
}

/** The chips of a composer's pending uploads. */
export function PendingChips({ items, onRemove }: { items: Pending[]; onRemove: (local: string) => void }) {
  if (!items.length) return null;
  return (
    <div className="att-chips pending">
      {items.map((x) => (
        <span key={x.local} className={`att ${x.error ? 'bad' : x.busy ? 'busy' : ''}`} title={x.error ?? x.name}>
          {x.preview ? <img src={x.preview} alt="" /> : <i className="k">{KIND_LABEL[x.kind ?? ''] ?? (x.name.split('.').pop() ?? '').toUpperCase()}</i>}
          <span className="n">{x.name}</span><small>{x.error ? 'failed' : x.busy ? 'uploading…' : fmtBytes(x.size)}</small>
          <button type="button" aria-label={`Remove ${x.name}`} onClick={() => onRemove(x.local)}>×</button>
        </span>
      ))}
    </div>
  );
}

/** The chips a stored note, decision or message shows. */
export function AttachmentChips({ list, size = 'm' }: { list?: Att[] | null; size?: 's' | 'm' }) {
  if (!list?.length) return null;
  return (
    <div className={`att-chips ${size}`}>
      {list.map((a) => a.kind === 'image' ? (
        <a key={a.id} className="att thumb" href={a.url} target="_blank" rel="noreferrer" title={a.filename}><img src={a.url} alt={a.filename} loading="lazy" /></a>
      ) : (
        <a key={a.id} className="att" href={a.url} target="_blank" rel="noreferrer" title={a.filename}><i className="k">{KIND_LABEL[a.kind] ?? a.kind}</i><span className="n">{a.filename}</span><small>{fmtBytes(a.size_bytes)}</small></a>
      ))}
    </div>
  );
}
